import { computeRevisionChecksum, type ServerAction } from "@skilitics/nautilus-core-designer-data";

import type { BackendApi } from "../../backend/index.ts";
import { getConflictChecksum, getConflictExpectedRevision } from "./errors.ts";
import { clearUnsentActions, loadUnsentActions, removeFirstUnsentAction } from "./unsentActionsStorage.ts";

export const sendRestoredAction = async (api: BackendApi, action: ServerAction) => {
  switch (action.type) {
    case "APPLY_REVISION": {
      const { checksum, ...rest } = action.payload;
      await api.applyRevision({
        ...rest,
        checksum: checksum ?? computeRevisionChecksum(rest.actions),
        appVersion: APP_VERSION,
      });
      break;
    }
    case "UNDO_REVISION":
      await api.undo(action.payload.revision);
      break;
    case "REDO_REVISION":
      await api.redo(action.payload.revision);
      break;
  }
};

export const isAlreadyAppliedError = (error: unknown, action: ServerAction): boolean => {
  const expectedRevision = getConflictExpectedRevision(error);
  if (expectedRevision === undefined) return false;

  // Can only verify when the server's current revision matches the action's revision exactly
  if (expectedRevision !== action.payload.revision) return false;

  const serverChecksum = getConflictChecksum(error);
  const clientChecksum = action.type === "APPLY_REVISION" ? action.payload.checksum : undefined;

  // If both checksums present, they must match; if either missing (old data), fall back to trusting revision number
  if (serverChecksum != null && clientChecksum != null) return serverChecksum === clientChecksum;
  return true;
};

/**
 * Re-delivers unsent actions from localStorage directly to the server.
 * Call `redeliver()` before loading entity data so getNodeDesigns() reflects the restored revisions.
 *
 * Actions that were in-flight when the page refreshed may have already been applied by the server.
 * A 409 where the server's expectedRevision >= the action's revision is treated as "already applied"
 * and skipped. Other errors propagate to the caller.
 */
export const createRedeliverUnsentServerActions = ({ api, hostId }: { api: BackendApi; hostId: string }) => {
  const redeliver = async () => {
    const stored = loadUnsentActions(hostId);
    if (!stored) return;

    for (const action of stored.actions) {
      try {
        await sendRestoredAction(api, action);
      } catch (error) {
        if (!isAlreadyAppliedError(error, action)) throw error;
        // 409 with expectedRevision >= action's revision — already applied before page refresh
      }
      removeFirstUnsentAction(hostId);
    }
    clearUnsentActions(hostId);
  };

  const discard = () => {
    clearUnsentActions(hostId);
  };

  return { redeliver, discard };
};
